import { AnimationEvent } from '@angular/animations';
import { ViewportRuler } from '@angular/cdk/overlay';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  NgZone,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';

import { DrawerContentComponent } from './drawer-content.component';
import { DrawerComponent, DrawerLeftDirective, DrawerRightDirective } from './drawer.component';
import { DrawerService } from './drawer.service';

@Component({
  selector: 'tt-drawer-container',
  template: `
    <ng-content select="tt-drawer ttDrawerLeft" />

    @if (drawerService.isLeftResizable()) {
      <div #leftResizeHandle class="tt-drawer-left-resize-handle"></div>
    }

    @if (drawerService.isBackdropVisible()) {
      <div class="tt-drawer_backdrop" (click)="backdropClicked.emit()"></div>
    }

    <ng-content select="tt-drawer-content" />

    @if (drawerService.isRightResizable()) {
      <div #rightResizeHandle class="tt-drawer-right-resize-handle"></div>
    }
    <ng-content select="tt-drawer" />
  `,
  styleUrls: ['./drawer.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DrawerService],
})
export class DrawerContainerComponent implements AfterContentInit {
  private ngZone = inject(NgZone);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private elementRef = inject(ElementRef);
  protected drawerService = inject(DrawerService);

  @Input() public showToggle = false;

  @Output() backdropClicked = new EventEmitter<void>();

  @HostBinding('class.tt-drawer-container') hostCls = true;

  @ContentChild(DrawerRightDirective, { read: DrawerComponent }) drawerRight: DrawerComponent;
  @ContentChild(DrawerLeftDirective, { read: DrawerComponent }) drawerLeft: DrawerComponent;

  @ContentChild(DrawerContentComponent) drawerContent: DrawerContentComponent;

  private leftResizeHandlePlaceholder: ElementRef;
  @ViewChild('leftResizeHandle') set leftResizeHandle(resizeHandler: ElementRef) {
    this.leftResizeHandlePlaceholder = resizeHandler;

    if (resizeHandler) {
      resizeHandler.nativeElement.addEventListener('mousedown', (event: MouseEvent) => {
        this.startResize(event, 'left');
        event.preventDefault();
      });
    }
  }

  private rightResizeHandlePlaceholder: ElementRef;
  @ViewChild('rightResizeHandle') set rightResizeHandle(resizeHandler: ElementRef) {
    this.rightResizeHandlePlaceholder = resizeHandler;

    if (resizeHandler) {
      resizeHandler.nativeElement.addEventListener('mousedown', (event: MouseEvent) => {
        this.startResize(event, 'right');
        event.preventDefault();
      });
    }
  }

  public contentMargins: { right: null | number; left: null | number } = { right: null, left: null };

  constructor() {
    const viewportRuler = inject(ViewportRuler);
    viewportRuler
      .change()
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.updateContentMargins();
      });
  }

  private updateContentMargins(side?: 'left' | 'right') {
    if (side === 'left' || !side) {
      this.updateContentLeftMargin();
      return;
    }

    if (side === 'right' || !side) {
      this.updateContentRightMargin();
      return;
    }
  }

  private updateContentLeftMargin() {
    if (!this.drawerLeft) {
      return;
    }

    const left = this.drawerLeft.getWidth();

    if (this.contentMargins.left !== left) {
      if (this.drawerService.leftDrawerMode() !== 'over') {
        this.drawerContent.setMarginLeft(left);
      } else {
        this.drawerContent.setMarginLeft(0);
      }

      if (this.leftResizeHandlePlaceholder) {
        this.leftResizeHandlePlaceholder.nativeElement.style.left = `${left}px`;
      }

      this.changeDetectorRef.markForCheck();
    }
  }

  private updateContentRightMargin() {
    if (!this.drawerRight) {
      return;
    }

    const right = this.drawerRight.getWidth();

    if (this.contentMargins.right !== right) {
      if (this.drawerService.rightDrawerMode() !== 'over') {
        this.drawerContent.setMarginRight(right);
      } else {
        this.drawerContent.setMarginRight(0);
      }

      if (this.rightResizeHandlePlaceholder) {
        this.rightResizeHandlePlaceholder.nativeElement.style.right = `${right}px`;
      }

      this.changeDetectorRef.markForCheck();
    }
  }

  public ngAfterContentInit() {
    if ((!this.drawerLeft && !this.drawerRight) || !this.drawerContent) {
      throw new Error('tt-drawer-container must contain a tt-drawer and a tt-drawer-content');
    }

    this.drawerLeft?._animationStarted.pipe(filter((event: AnimationEvent) => event.fromState !== event.toState)).subscribe(() => {
      this.updateContentMargins('left');
      this.changeDetectorRef.markForCheck();
    });

    this.drawerRight?._animationStarted.pipe(filter((event: AnimationEvent) => event.fromState !== event.toState)).subscribe(() => {
      this.updateContentMargins('right');
      this.changeDetectorRef.markForCheck();
    });
  }

  private startResize(event: MouseEvent, side: 'left' | 'right') {
    this.drawerContent.elementRef.nativeElement.style.transition = 'none';

    const startWidth = side === 'right' ? this.drawerRight.getWidth() : this.drawerLeft.getWidth();

    const mouseMoveListener = (moveEvent: MouseEvent) => {
      const diff = side === 'right' ? moveEvent.clientX - event.clientX : event.clientX - moveEvent.clientX;
      const newWidth = startWidth - diff;

      const elementWidth = this.elementRef.nativeElement.offsetWidth;
      const combinedWidth = newWidth + (side === 'right' ? this.drawerRight.getWidth() : this.drawerLeft.getWidth());

      // if the newWidth is bigger then the combined width of the drawers + 50px, don't resize
      if (elementWidth - combinedWidth < 200) {
        return;
      }

      if (side === 'right') {
        this.drawerRight.setWidth(newWidth);
        this.updateContentMargins('right');
      } else {
        this.drawerLeft.setWidth(newWidth);
        this.updateContentMargins('left');
      }
    };

    const mouseUpListener = () => {
      document.removeEventListener('mousemove', mouseMoveListener);
      document.removeEventListener('mouseup', mouseUpListener);

      this.ngZone.runOutsideAngular(() => {
        this.drawerContent.elementRef.nativeElement.style.transition = '';
      });
    };

    document.addEventListener('mousemove', mouseMoveListener);
    document.addEventListener('mouseup', mouseUpListener);
  }
}
